import React from "react";
import { AlertTriangle } from "lucide-react";
import { markWebGLUnsupported } from "@/lib/webgl";

/**
 * GlobeErrorBoundary — catches any runtime error thrown inside the 3D globe
 * subtree (WebGL init failure, three.js cleanup race on fast view-switches,
 * missing GPU, blocked CDN texture, etc.).
 *
 * If a WebGL / three error is detected, we automatically invoke `onFallback`
 * so the parent can seamlessly swap to the 2D SVG map. The error card only
 * ever shows if the parent doesn't provide a fallback handler.
 */
export default class GlobeErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "", didFallback: false };
  }
  static getDerivedStateFromError(err) {
    return { hasError: true, message: err?.message || String(err) };
  }
  componentDidCatch(err, info) {
    // eslint-disable-next-line no-console
    console.error("ThreatGlobe crash:", err, info);
    const msg = String(err?.message || err || "").toLowerCase();
    const isWebGL =
      msg.includes("webgl") ||
      msg.includes("three") ||
      msg.includes("context") ||
      msg.includes("gl_") ||
      msg.includes("shader");
    if (isWebGL) {
      markWebGLUnsupported();
    }
    // Auto-fallback to 2D whenever the parent gave us a handler — this is what
    // users expect: the wall keeps working, we don't strand them on an error card.
    if (this.props.onFallback && !this.state.didFallback) {
      this.setState({ didFallback: true }, () => {
        try {
          this.props.onFallback({ reason: isWebGL ? "webgl" : "runtime", message: err?.message });
        } catch { /* ignore */ }
      });
    }
  }
  render() {
    if (!this.state.hasError) return this.props.children;
    // Auto-fallback path: render nothing while parent swaps to 2D.
    if (this.state.didFallback) return null;
    return (
      <div className="w-full h-full flex items-center justify-center bg-[#060606]"
           data-testid="threat-globe-error">
        <div className="max-w-md text-center p-8">
          <AlertTriangle className="w-10 h-10 text-[#FFC542] mx-auto mb-3" />
          <div className="font-display font-black text-2xl mb-2">
            3D Globe couldn&apos;t start
          </div>
          <p className="text-sm text-[#8E9196] mb-4 font-mono">
            {this.state.message ||
              "WebGL failed to initialise on this device or the browser blocked the earth-night texture."}
          </p>
          <p className="text-xs text-[#52525B] mb-4">
            Possible causes: hardware acceleration is off, GPU driver is unresponsive, an ad-blocker is blocking the texture CDN, or the browser doesn&apos;t support WebGL2.
          </p>
          {this.props.onFallback && (
            <button
              onClick={() => this.props.onFallback({ reason: "manual" })}
              data-testid="threat-globe-fallback-btn"
              className="px-4 py-2 bg-[#00F0FF] text-black text-[10px] font-mono uppercase tracking-widest rounded-sm hover:bg-white transition-colors"
            >
              Switch to 2D map
            </button>
          )}
        </div>
      </div>
    );
  }
}
